import { configureStore } from '@reduxjs/toolkit';
import { compareQuotesReducer } from '../../components/CompareQuotes/compareQuotes.slice';
import { financialDetailsFormSlice } from '../../components/FinancialDetailsForm/financialDetailsForm.slice';
import { financialProductFormSlice } from '../../components/FinancialProductForm/financialProductForm.slice';
import { summaryConsentFormSlice } from '../../components/SummaryConsentForm/summaryConsentForm.slice';
import { documentUploadPageSlice } from '../../screens/NewQuotation/DocumentUploadPage/DocumentUploadPage.slice';
import { affordabilityDocumentUploadPageSlice } from '../../screens/NewQuotation/AffordabilityDocumentUploadPage/AffordabilityDocumentUploadPage.slice';
import { financialProductPageSlice } from '../../screens/NewQuotation/FinancialProductPage/financialProductPage.slice';
import { newCarConfiguratorPageSlice } from '../../screens/NewQuotation/NewCarConfiguratorPage/newCarConfiguratorPage.slice';
import { usedCarFormSelectorPageSlice } from '../../screens/NewQuotation/UsedCarFormSelectorPage/UsedCarFormSelectorPage.slice';
import { newQuotationSlice, newQuotationSliceActions } from '../../screens/NewQuotation/newQuotation.slice';
import { apiSlice } from './features/api/api.api-slice';
import { authSlice } from './features/auth.slice';
import { journeyDataActions, journeyDataSlice } from './features/journeyData.slice';
import { notesSlice } from './features/notes.slice';
import { outletReducer } from './features/outlet.slice';
import { transactionSlice } from './features/transaction.slice';
import { uiStateActions, uiStateSlice } from './features/uiState.slice';
import { brokerReducer } from './features/broker.slice';

export const store = configureStore({
  reducer: {
    usedCarFormSelectorPage: usedCarFormSelectorPageSlice.reducer,
    financialProductForm: financialProductFormSlice.reducer,
    financialProductPage: financialProductPageSlice.reducer,
    newCarConfiguratorPage: newCarConfiguratorPageSlice.reducer,
    newQuotation: newQuotationSlice.reducer,
    compareQuotes: compareQuotesReducer,
    auth: authSlice.reducer,
    outlets: outletReducer,
    financialDetailsForm: financialDetailsFormSlice.reducer,
    summaryConsentForm: summaryConsentFormSlice.reducer,
    documentUploadPage: documentUploadPageSlice.reducer,
    affordabilityDocumentUploadPage: affordabilityDocumentUploadPageSlice.reducer,
    transaction: transactionSlice.reducer,
    notes: notesSlice.reducer,
    api: apiSlice.reducer,
    journeyData: journeyDataSlice.reducer,
    uiState: uiStateSlice.reducer,
    broker: brokerReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(apiSlice.middleware),
});

export type RootState = ReturnType<typeof store.getState>;

const storeResetHandlers: Record<keyof RootState, () => void> = {
  uiState() {
    store.dispatch(uiStateActions.resetUiState());
  },
  journeyData() {
    store.dispatch(journeyDataActions.resetJourneyData());
  },
  newQuotation() {
    store.dispatch(newQuotationSliceActions.resetNewQuotation());
  },
  broker() {
    /* noop */
  },
  usedCarFormSelectorPage() {
    /* noop */
  },
  financialProductForm() {
    /* noop */
  },
  financialProductPage() {
    /* noop */
  },
  newCarConfiguratorPage() {
    /* noop */
  },
  compareQuotes() {
    /* noop */
  },
  auth() {
    /* noop */
  },
  outlets() {
    /* noop */
  },
  financialDetailsForm() {
    /* noop */
  },
  summaryConsentForm() {
    /* noop */
  },
  documentUploadPage() {
    /* noop */
  },
  affordabilityDocumentUploadPage() {
    /* noop */
  },
  transaction() {
    /* noop */
  },
  notes() {
    /* noop */
  },
  api() {
    /* noop */
  },
};

export const resetQuoteSpecificReduxData = () => {
  Object.values(storeResetHandlers).forEach((resetHandler) => resetHandler());
};

/**
 * - Wrapper for selectors for clearly traceable access to store.
 * - Static means it does not observe store value and is not reactive.
 */
export const staticSelector = <Selected = unknown>(selector: (state: RootState) => Selected) => {
  return selector(store.getState());
};
